import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth, useBlogApi } from '../hooks/useBlog';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, 
  Save, 
  Eye, 
  Upload,
  X,
  Image as ImageIcon,
  Bold,
  Italic,
  List,
  Link as LinkIcon,
  Heading2
} from 'lucide-react';

const AdminPostEditor = () => {
  const { postId } = useParams();
  const isEditing = postId && postId !== 'new';
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const { adminGetPost, createPost, updatePost, uploadImage } = useBlogApi();
  const fileInputRef = useRef(null);
  const contentRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('General');
  const [tags, setTags] = useState('');
  const [author, setAuthor] = useState('Kuldeepsinh Jhala');
  const [featuredImage, setFeaturedImage] = useState('');
  const [status, setStatus] = useState('draft');

  const categories = [
    'General',
    'Finance Tips',
    'Tax Planning',
    'Business Growth',
    'M&A Insights',
    'Industry News',
    'Case Studies'
  ];

  useEffect(() => {
    const init = async () => {
      const isAuth = await checkAuth();
      if (!isAuth) {
        navigate('/admin/login');
        return;
      }
      
      if (isEditing) {
        loadPost();
      }
    };
    init();
  }, [checkAuth, navigate, isEditing, postId]);

  const loadPost = async () => {
    try {
      setLoading(true);
      const post = await adminGetPost(postId);
      setTitle(post.title);
      setSlug(post.slug);
      setExcerpt(post.excerpt);
      setContent(post.content);
      setCategory(post.category);
      setTags(post.tags.join(', '));
      setAuthor(post.author);
      setFeaturedImage(post.featured_image || '');
      setStatus(post.status);
    } catch (error) {
      setError('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    if (!isEditing) {
      setSlug(generateSlug(newTitle));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const result = await uploadImage(file);
      setFeaturedImage(result.url);
    } catch (error) {
      setError('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Simple formatting helpers
  const insertFormat = (before, after = '') => {
    const textarea = contentRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    const newText = content.substring(0, start) + before + selectedText + after + content.substring(end);
    setContent(newText);
    
    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, end + before.length);
    }, 0);
  };

  const handleSave = async (publishStatus = status) => {
    if (!title || !slug || !excerpt || !content) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const postData = {
        title,
        slug,
        excerpt,
        content,
        category,
        tags: tags.split(',').map(t => t.trim()).filter(t => t),
        author,
        featured_image: featuredImage || null,
        status: publishStatus
      };

      if (isEditing) {
        await updatePost(postId, postData);
      } else {
        await createPost(postData);
      }

      navigate('/admin/dashboard');
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center">
        <p className="font-body text-[#6B6860]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      {/* Header */}
      <header className="bg-white border-b border-[#D4D1C9] sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="p-2 hover:bg-[#F9F6F0] rounded">
              <ArrowLeft className="w-5 h-5 text-[#6B6860]" />
            </Link>
            <h1 className="font-heading text-lg font-medium text-[#1A1A18]">
              {isEditing ? 'Edit Post' : 'New Post'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => handleSave('draft')}
              disabled={saving}
              className="bg-white border border-[#D4D1C9] text-[#1A1A18] hover:bg-[#F9F6F0] font-body rounded-none"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Draft
            </Button>
            <Button
              onClick={() => handleSave('published')}
              disabled={saving}
              data-testid="publish-post-btn"
              className="bg-[#2C4A3E] hover:bg-[#2C4A3E]/90 text-white font-body rounded-none"
            >
              <Eye className="w-4 h-4 mr-2" />
              {status === 'published' ? 'Update' : 'Publish'}
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200">
            <p className="font-body text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div className="bg-white border border-[#D4D1C9] p-6">
              <label className="font-body text-sm text-[#6B6860] block mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={handleTitleChange}
                data-testid="post-title-input"
                className="w-full border-b border-[#D4D1C9] bg-transparent px-0 py-2 font-heading text-2xl focus:outline-none focus:border-[#2C4A3E]"
                placeholder="Enter post title..."
              />
            </div>

            {/* Excerpt */}
            <div className="bg-white border border-[#D4D1C9] p-6">
              <label className="font-body text-sm text-[#6B6860] block mb-2">
                Excerpt <span className="text-red-500">*</span>
              </label>
              <textarea
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                data-testid="post-excerpt-input"
                rows={3}
                className="w-full border border-[#D4D1C9] bg-transparent p-3 font-body text-base focus:outline-none focus:border-[#2C4A3E] resize-none"
                placeholder="Brief summary of the post..."
              />
            </div>

            {/* Content Editor */}
            <div className="bg-white border border-[#D4D1C9] p-6">
              <label className="font-body text-sm text-[#6B6860] block mb-2">
                Content <span className="text-red-500">*</span>
              </label>
              
              {/* Formatting Toolbar */}
              <div className="flex items-center gap-1 mb-3 pb-3 border-b border-[#D4D1C9]">
                <button
                  type="button"
                  onClick={() => insertFormat('<strong>', '</strong>')}
                  className="p-2 hover:bg-[#F9F6F0] rounded"
                  title="Bold"
                >
                  <Bold className="w-4 h-4 text-[#6B6860]" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormat('<em>', '</em>')}
                  className="p-2 hover:bg-[#F9F6F0] rounded"
                  title="Italic"
                >
                  <Italic className="w-4 h-4 text-[#6B6860]" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormat('<h2>', '</h2>')}
                  className="p-2 hover:bg-[#F9F6F0] rounded"
                  title="Heading"
                >
                  <Heading2 className="w-4 h-4 text-[#6B6860]" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormat('<ul>\n<li>', '</li>\n</ul>')}
                  className="p-2 hover:bg-[#F9F6F0] rounded"
                  title="List"
                >
                  <List className="w-4 h-4 text-[#6B6860]" />
                </button>
                <button
                  type="button"
                  onClick={() => insertFormat('<a href="">', '</a>')}
                  className="p-2 hover:bg-[#F9F6F0] rounded"
                  title="Link"
                >
                  <LinkIcon className="w-4 h-4 text-[#6B6860]" />
                </button>
                <span className="text-xs text-[#6B6860] ml-4">Supports HTML formatting</span>
              </div>
              
              <textarea
                ref={contentRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                data-testid="post-content-input"
                rows={20}
                className="w-full border border-[#D4D1C9] bg-transparent p-4 font-body text-base focus:outline-none focus:border-[#2C4A3E] resize-none font-mono"
                placeholder="Write your article content here... HTML formatting is supported."
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Featured Image */}
            <div className="bg-white border border-[#D4D1C9] p-6">
              <label className="font-body text-sm text-[#6B6860] block mb-4">Featured Image</label>
              
              {featuredImage ? (
                <div className="relative">
                  <img 
                    src={featuredImage} 
                    alt="Featured" 
                    className="w-full h-40 object-cover"
                  />
                  <button
                    onClick={() => setFeaturedImage('')}
                    className="absolute top-2 right-2 p-1 bg-white rounded-full shadow"
                  >
                    <X className="w-4 h-4 text-[#6B6860]" />
                  </button>
                </div>
              ) : (
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-[#D4D1C9] p-8 text-center cursor-pointer hover:border-[#2C4A3E] transition-colors"
                >
                  {uploading ? (
                    <p className="font-body text-sm text-[#6B6860]">Uploading...</p>
                  ) : (
                    <>
                      <ImageIcon className="w-8 h-8 text-[#D4D1C9] mx-auto mb-2" />
                      <p className="font-body text-sm text-[#6B6860]">Click to upload</p>
                    </>
                  )}
                </div>
              )}
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
              />
            </div>

            {/* Slug */}
            <div className="bg-white border border-[#D4D1C9] p-6">
              <label className="font-body text-sm text-[#6B6860] block mb-2">
                URL Slug <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(generateSlug(e.target.value))}
                data-testid="post-slug-input"
                className="w-full border border-[#D4D1C9] bg-transparent p-2 font-body text-sm focus:outline-none focus:border-[#2C4A3E]"
                placeholder="post-url-slug"
              />
              <p className="font-body text-xs text-[#6B6860] mt-2">/blog/{slug || 'post-url'}</p>
            </div>

            {/* Category */}
            <div className="bg-white border border-[#D4D1C9] p-6">
              <label className="font-body text-sm text-[#6B6860] block mb-2">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full border border-[#D4D1C9] bg-transparent p-2 font-body text-sm focus:outline-none focus:border-[#2C4A3E]"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div className="bg-white border border-[#D4D1C9] p-6">
              <label className="font-body text-sm text-[#6B6860] block mb-2">Tags</label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="w-full border border-[#D4D1C9] bg-transparent p-2 font-body text-sm focus:outline-none focus:border-[#2C4A3E]"
                placeholder="tag1, tag2, tag3"
              />
              <p className="font-body text-xs text-[#6B6860] mt-2">Separate tags with commas</p>
            </div>

            {/* Author */}
            <div className="bg-white border border-[#D4D1C9] p-6">
              <label className="font-body text-sm text-[#6B6860] block mb-2">Author</label>
              <input
                type="text"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                className="w-full border border-[#D4D1C9] bg-transparent p-2 font-body text-sm focus:outline-none focus:border-[#2C4A3E]"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPostEditor;
